/**
 * Persists uploaded track audio in IndexedDB for WL demo (no S3).
 * `resource` / track detail stores refs like `wl-demo-blob:<encodedStorageKey>`.
 */

const DB_NAME = "wl_demo_track_audio";
const STORE = "blobs";
const DB_VERSION = 1;

export const WL_DEMO_BLOB_PREFIX = "wl-demo-blob:";

export function isWlDemoBlobRef(value) {
  return typeof value === "string" && value.startsWith(WL_DEMO_BLOB_PREFIX);
}

export function wlDemoBlobStorageKeyFromRef(ref) {
  if (!isWlDemoBlobRef(ref)) {
    return "";
  }
  return decodeURIComponent(ref.slice(WL_DEMO_BLOB_PREFIX.length));
}

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("indexedDB unavailable"));
      return;
    }
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
  });
}

export async function putDemoTrackBlob(storageKey, blob) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readwrite");
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => reject(tx.error);
    tx.objectStore(STORE).put(blob, storageKey);
  });
}

export async function getDemoTrackBlob(storageKey) {
  if (!storageKey) {
    return null;
  }
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE, "readonly");
    const r = tx.objectStore(STORE).get(storageKey);
    r.onsuccess = () => {
      db.close();
      resolve(r.result || null);
    };
    r.onerror = () => reject(r.error);
  });
}

export async function clearAllDemoTrackBlobs() {
  if (typeof indexedDB === "undefined") {
    return;
  }
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.oncomplete = () => {
        db.close();
        resolve();
      };
      tx.onerror = () => reject(tx.error);
      tx.objectStore(STORE).clear();
    });
  } catch (e) {
    console.warn("[demoTrackBlobStore] clear failed", e);
  }
}
